import { motion } from "framer-motion";
import { useState } from "react";
import { useText } from "../context/ContentContext";

const steps = [
    {
        n: "01",
        title: "Intake",
        body:
            "Complete a brief, considered questionnaire highlighting your goals, timeline, and the location you want to buy or sell in.",
        image:
            "https://images.unsplash.com/photo-1638284457192-27d3d0ec51aa?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    },
    {
        n: "02",
        title: "Matchmaking",
        body:
            "We meet by video call, then hand-research the local market and shortlist up to three agents who match your profile.",
        image:
            "https://images.unsplash.com/photo-1774685110718-c5b4fe026144?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    },
    {
        n: "03",
        title: "Introductions",
        body:
            "Meet your matches on your timeline. Ask hard questions, compare approaches, and choose the one you trust most.",
        image:
            "https://images.pexels.com/photos/8815909/pexels-photo-8815909.jpeg?auto=compress&cs=tinysrgb&w=1200",
    },
    {
        n: "04",
        title: "Success",
        body:
            "Begin your real estate journey with confidence — backed by a top-tier professional and an experienced partner in your corner.",
        image:
            "https://images.pexels.com/photos/7578903/pexels-photo-7578903.jpeg?auto=compress&cs=tinysrgb&w=1200",
    },
];

export default function ClientJourney() {
    const [active, setActive] = useState(0);
    const eyebrow = useText("journey_eyebrow", "The Client Journey");
    const title = useText("journey_title", "A Smarter Way to Find Your Realtor");
    const subtitle = useText(
        "journey_subtitle",
        "A personalized 4-step process designed to remove the guesswork from your move."
    );

    return (
        <section
            id="journey"
            className="bg-brand-sand"
            data-testid="client-journey-section"
        >
            <div className="mx-auto max-w-7xl px-6 lg:px-10 py-24 lg:py-32">
                <div className="grid lg:grid-cols-12 gap-8 mb-16">
                    <div className="lg:col-span-5">
                        <div className="text-xs uppercase tracking-[0.25em] text-brand-gold font-semibold mb-5">
                            — {eyebrow}
                        </div>
                        <h2 className="font-serif text-4xl lg:text-5xl tracking-tight leading-tight text-brand-ink">
                            {title}
                        </h2>
                    </div>
                    <div className="lg:col-span-6 lg:col-start-7 flex items-end">
                        <p className="text-lg text-brand-muted leading-relaxed">
                            {subtitle}
                        </p>
                    </div>
                </div>

                <div className="grid lg:grid-cols-12 gap-12 items-start">
                    {/* Steps list */}
                    <div className="lg:col-span-6 space-y-2">
                        {steps.map((step, i) => {
                            const isActive = active === i;
                            return (
                                <motion.button
                                    key={step.n}
                                    onMouseEnter={() => setActive(i)}
                                    onFocus={() => setActive(i)}
                                    onClick={() => setActive(i)}
                                    data-testid={`journey-step-${i}`}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: "-80px" }}
                                    transition={{ duration: 0.6, delay: i * 0.1 }}
                                    className={`block w-full text-left border-t border-brand-line py-7 group transition-colors ${
                                        isActive ? "bg-brand-paper/50" : ""
                                    } ${i === steps.length - 1 ? "border-b" : ""}`}
                                >
                                    <div className="flex items-start gap-6 px-2">
                                        <div className="font-serif text-3xl text-brand-gold w-14 shrink-0">
                                            {step.n}
                                        </div>
                                        <div className="flex-1">
                                            <h3 className="font-serif text-2xl lg:text-3xl text-brand-ink leading-tight">
                                                {step.title}
                                            </h3>
                                            <p
                                                className={`text-brand-muted mt-2 text-base leading-relaxed transition-all overflow-hidden ${
                                                    isActive
                                                        ? "max-h-40 opacity-100"
                                                        : "max-h-0 lg:max-h-0 opacity-0 lg:opacity-0"
                                                }`}
                                            >
                                                {step.body}
                                            </p>
                                            <p className="lg:hidden text-brand-muted mt-2 text-base leading-relaxed">
                                                {step.body}
                                            </p>
                                        </div>
                                        <div
                                            className={`text-brand-ink text-xl transition-transform ${
                                                isActive ? "rotate-45" : ""
                                            }`}
                                        >
                                            +
                                        </div>
                                    </div>
                                </motion.button>
                            );
                        })}
                    </div>

                    {/* Image panel */}
                    <div className="lg:col-span-6 sticky top-28">
                        <div className="relative image-zoom h-[480px] lg:h-[600px]">
                            {steps.map((step, i) => (
                                <motion.img
                                    key={step.n}
                                    src={step.image}
                                    alt={step.title}
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: active === i ? 1 : 0 }}
                                    transition={{ duration: 0.7 }}
                                    className="absolute inset-0 w-full h-full object-cover"
                                />
                            ))}
                            <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between text-brand-paper">
                                <div className="text-xs uppercase tracking-[0.25em] bg-brand-ink/80 px-3 py-2 backdrop-blur">
                                    Step {steps[active].n}
                                </div>
                                <div className="font-serif text-2xl bg-brand-ink/80 px-4 py-2 backdrop-blur">
                                    {steps[active].title}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
